import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    CardHeader,
    Button,
    Alert,
    CircularProgress,
    useTheme as useMuiTheme,
    useMediaQuery,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import {
    AdminPanelSettings as AdminIcon,
    MonetizationOn as PricingIcon,
    Receipt as InvoiceIcon,
    Pin as PinIcon,
    Store as BranchIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
    runUpdatePricing,
    runUpdateMonthlyInvoices,
    runUpdateRiderPins,
    switchBranch,
} from '../../store/slices/adminSlice';
import { updateBranchId } from '../../store/slices/authSlice';
import { useTheme } from '../../contexts/ThemeContext';
import Toast from '../../components/Toast';
import useToast from '../../hooks/useToast';
import GenericModal from '../../components/GenericModal';
import GenericButton from '../../components/GenericButton';
import { branchApi, Branch } from '../../services/branchApi';

const AdminActions = () => {
    const muiTheme = useMuiTheme();
    const { theme: appTheme } = useTheme();
    const dispatch = useAppDispatch();
    const { pricingLoading, invoicesLoading, riderPinsLoading, switchBranchLoading } = useAppSelector((state) => state.admin);
    const { user } = useAppSelector((state) => state.auth);
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const { toast, showSuccess, showError, hideToast } = useToast();

    // Confirmation dialog state
    const [confirmDialog, setConfirmDialog] = useState<{
        open: boolean;
        title: string;
        message: string;
        onConfirm: () => void;
    }>({ open: false, title: '', message: '', onConfirm: () => {} });

    // Branch switch state
    const [branches, setBranches] = useState<Branch[]>([]);
    const [selectedBranchId, setSelectedBranchId] = useState<number | ''>(user?.branch_id || '');
    const [branchesLoading, setBranchesLoading] = useState(false);

    // Fetch branches on mount
    useEffect(() => {
        const fetchBranches = async () => {
            setBranchesLoading(true);
            try {
                const response = await branchApi.getBranches({ per_page: 100 });
                setBranches(response.branches);
            } catch {
                showError('Failed to load branches');
            } finally {
                setBranchesLoading(false);
            }
        };
        fetchBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleUpdatePricing = () => {
        setConfirmDialog({
            open: true,
            title: 'Update Pricing',
            message: 'This will calculate COD amounts, service fees, and GST for all pending consignments across all clients. Are you sure you want to proceed?',
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, open: false }));
                try {
                    await dispatch(runUpdatePricing()).unwrap();
                    showSuccess('Customer pricing update has been queued successfully.');
                } catch (err) {
                    showError(err instanceof Error ? err.message : String(err));
                }
            },
        });
    };

    const handleUpdateMonthlyInvoices = () => {
        setConfirmDialog({
            open: true,
            title: 'Update Monthly Invoices',
            message: 'This will generate monthly invoices for all clients with Monthly invoicing enabled. Are you sure you want to proceed?',
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, open: false }));
                try {
                    await dispatch(runUpdateMonthlyInvoices()).unwrap();
                    showSuccess('Monthly invoice generation has been queued successfully.');
                } catch (err) {
                    showError(err instanceof Error ? err.message : String(err));
                }
            },
        });
    };

    const handleUpdateRiderPins = () => {
        setConfirmDialog({
            open: true,
            title: 'Update Rider PINs',
            message: 'This will regenerate PIN codes for ALL riders. Existing PINs will be replaced. Are you sure you want to proceed?',
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, open: false }));
                try {
                    await dispatch(runUpdateRiderPins()).unwrap();
                    showSuccess('Rider PIN regeneration has been queued successfully.');
                } catch (err) {
                    showError(err instanceof Error ? err.message : String(err));
                }
            },
        });
    };

    const handleSwitchBranch = () => {
        if (!selectedBranchId) {
            showError('Please select a branch first.');
            return;
        }

        if (selectedBranchId === user?.branch_id) {
            showError('You are already in this branch.');
            return;
        }

        const branchName = branches.find(b => b.id === selectedBranchId)?.name || 'selected branch';

        setConfirmDialog({
            open: true,
            title: 'Switch Branch',
            message: `Are you sure you want to switch your active branch to "${branchName}"?`,
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, open: false }));
                try {
                    const result = await dispatch(switchBranch(selectedBranchId as number)).unwrap();
                    dispatch(updateBranchId(result.branch_id));
                    showSuccess(`Branch switched successfully to ${result.branch_name}`);
                    setSelectedBranchId('');
                } catch (err) {
                    showError(err instanceof Error ? err.message : String(err));
                }
            },
        });
    };

    const cardStyles = {
        bgcolor: muiTheme.palette.background.paper,
        border: `1px solid ${muiTheme.palette.divider}`,
        height: '100%',
    };

    const cardHeaderStyles = {
        bgcolor: muiTheme.palette.action.hover,
        '& .MuiCardHeader-title': {
            fontWeight: 600,
            fontSize: '1.1rem',
        },
    };

    return (
        <Box sx={{ p: isMobile ? 2 : 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography
                    variant={isMobile ? 'h5' : 'h4'}
                    sx={{
                        color: appTheme.palette.text.primary,
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <AdminIcon />
                    Admin Actions
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    System-wide administrative operations
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {/* Update Pricing */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card sx={cardStyles}>
                        <CardHeader
                            avatar={<PricingIcon color="primary" />}
                            title="Update Pricing"
                            sx={cardHeaderStyles}
                        />
                        <CardContent>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Calculate COD amounts, service fees, and GST for all pending consignments.
                                Creates customer ledger entries and consignment pricing records.
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    onClick={handleUpdatePricing}
                                    disabled={pricingLoading}
                                    startIcon={pricingLoading ? <CircularProgress size={20} color="inherit" /> : <PricingIcon />}
                                    sx={{
                                        bgcolor: appTheme.palette.secondary.main,
                                        '&:hover': { bgcolor: appTheme.palette.secondary.dark },
                                    }}
                                >
                                    {pricingLoading ? 'Processing...' : 'Run Update Pricing'}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Update Monthly Invoices */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card sx={cardStyles}>
                        <CardHeader
                            avatar={<InvoiceIcon color="primary" />}
                            title="Update Monthly Invoices"
                            sx={cardHeaderStyles}
                        />
                        <CardContent>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Generate monthly invoices for all clients with Monthly invoicing.
                                Links pending consignments and debit/credit notes to new invoices.
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    onClick={handleUpdateMonthlyInvoices}
                                    disabled={invoicesLoading}
                                    startIcon={invoicesLoading ? <CircularProgress size={20} color="inherit" /> : <InvoiceIcon />}
                                    sx={{
                                        bgcolor: appTheme.palette.secondary.main,
                                        '&:hover': { bgcolor: appTheme.palette.secondary.dark },
                                    }}
                                >
                                    {invoicesLoading ? 'Processing...' : 'Run Monthly Invoices'}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Update Rider PINs */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card sx={cardStyles}>
                        <CardHeader
                            avatar={<PinIcon color="primary" />}
                            title="Update Rider PINs"
                            sx={cardHeaderStyles}
                        />
                        <CardContent>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Regenerate unique 4-digit PIN codes for all riders.
                                All existing PINs will be replaced with new ones.
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    onClick={handleUpdateRiderPins}
                                    disabled={riderPinsLoading}
                                    startIcon={riderPinsLoading ? <CircularProgress size={20} color="inherit" /> : <PinIcon />}
                                    color="warning"
                                >
                                    {riderPinsLoading ? 'Processing...' : 'Regenerate All PINs'}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Switch Branch */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card sx={cardStyles}>
                        <CardHeader
                            avatar={<BranchIcon color="primary" />}
                            title="Switch Branch"
                            sx={cardHeaderStyles}
                        />
                        <CardContent>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Change your active branch. This affects which data you see and manage.
                            </Typography>

                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Select Branch</InputLabel>
                                <Select
                                    value={selectedBranchId}
                                    onChange={(e) => setSelectedBranchId(e.target.value as number)}
                                    label="Select Branch"
                                    disabled={branchesLoading}
                                >
                                    {branches.map((branch) => (
                                        <MenuItem key={branch.id} value={branch.id}>
                                            {branch.name} {branch.city ? `(${branch.city.name})` : ''}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    onClick={handleSwitchBranch}
                                    disabled={switchBranchLoading || !selectedBranchId}
                                    startIcon={switchBranchLoading ? <CircularProgress size={20} color="inherit" /> : <BranchIcon />}
                                    color="error"
                                >
                                    {switchBranchLoading ? 'Switching...' : 'Switch Branch'}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Confirmation Modal */}
            <GenericModal
                open={confirmDialog.open}
                onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
                title={confirmDialog.title}
                maxWidth="sm"
                actions={
                    <>
                        <GenericButton
                            variant="outline"
                            onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
                        >
                            Cancel
                        </GenericButton>
                        <GenericButton
                            variant="primary"
                            onClick={confirmDialog.onConfirm}
                        >
                            Yes, Proceed
                        </GenericButton>
                    </>
                }
            >
                <Typography variant="body1" color="text.primary">
                    {confirmDialog.message}
                </Typography>
            </GenericModal>

            {/* Toast Notification */}
            <Toast
                open={toast.open}
                message={toast.message}
                title={toast.title}
                severity={toast.severity}
                onClose={hideToast}
            />
        </Box>
    );
};

export default AdminActions;
